/**
 * Tells Sentry WHO an error happened to, and on WHICH native build.
 *
 * Without this every event was anonymous: "how many customers hit this?" and
 * "is it the same account every time?" could not be answered from the issue,
 * and an error reported from the app could not be told from the same error on
 * the web. Same shape as `microsoft-clarity.client.ts`: reactive to a login
 * mid-session and to an account switch, cleared on sign-out.
 *
 * What is sent is an opaque account id and nothing else — no name, no e-mail,
 * no address. The id is what support needs to open the account in the Super
 * Admin Console; the rest is PII an error tracker has no use for.
 */
import * as Sentry from '@sentry/nuxt'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { useUserStore } from '~/store/user'

export default defineNuxtPlugin(() => {
  const userStore = useUserStore()

  const identify = (user: Record<string, any> | undefined) => {
    const client = user?.client as Record<string, any> | undefined
    const id = client?.id ?? client?.userid
    Sentry.setUser(id ? { id: String(id) } : null)
  }

  identify(userStore.getUser as any)
  watch(
    () => userStore.getUser,
    (user) => identify(user as any),
  )

  if (Capacitor.isNativePlatform()) {
    // Which BINARY, beside the bundle's release: an OTA bundle runs on every
    // installed version at once, and a WebView bug is the binary's, not ours.
    App.getInfo()
      .then((info) => {
        Sentry.setTag('native.version', info.version)
        Sentry.setTag('native.build', info.build)
      })
      .catch(() => {
        /* An old binary without the App plugin still reports; it is just unnamed. */
      })
  }
})
